import { useEffect, useRef } from "react";
import { forwardRef } from "react";
import { TextField, InputAdornment } from "@mui/material";
import NumberFormat from "react-number-format";

const TextFieldMedidas = (props) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef?.current) {
      const currentInputRef = inputRef.current;
      const handleFocus = (event) => {
        const length = event.target.value?.length;
        event.target.setSelectionRange(length, length);
      };

      const handleMouseDown = (event) => {
        event.preventDefault();
        currentInputRef.focus();
        handleFocus(event);
      };

      currentInputRef.addEventListener("mousedown", handleMouseDown);
      return () => {
        currentInputRef.removeEventListener("mousedown", handleMouseDown);
      };
    }
  }, []);

  return (
    <TextField
      id={props.id}
      inputRef={inputRef}
      label={props.label}
      name={props.name}
      className={props.className}
      size={props.size}
      variant={props.variant}
      margin={props.margin ?? "normal"}
      disabled={props.disabled}
      error={props.error}
      required={props.required}
      onBlur={props.onBlur}
      onKeyDown={(event) => {
        const length = event.target.value?.length || 0;
        const acoes = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];
        if (acoes.includes(event.key)) {
          event.preventDefault();
          event.target.setSelectionRange(length, length);
        } else if (event.key === "Backspace") {
          const value = inputRef.current.value;
          const ultimoDigito = value.lastIndexOf(/\d/);
          const newValue =
            value.substring(0, ultimoDigito) +
            "0" +
            value.substring(ultimoDigito + 1);
          inputRef.current.value = newValue;
          inputRef.current.setSelectionRange(ultimoDigito, ultimoDigito);
        }
        if (props.onKeyDown) props.onKeyDown(event);
      }}
      onChange={props.onChange}
      value={parseFloat(props.value).toFixed(props.decimalScale || 2)}
      InputLabelProps={{ shrink: props.shrink, ...props.InputLabelProps }}
      InputProps={{
        inputComponent: MeasureFormatInput,
        endAdornment: props.unit ? (
          <InputAdornment position="end">{props.unit}</InputAdornment>
        ) : null,
        inputProps: {
          decimalScale: props.decimalScale ?? 2,
          fracionavel: props.fracionavel ?? true,
        },
      }}
      fullWidth
    />
  );
};

const MeasureFormatInput = forwardRef((props, ref) => {
  const { decimalScale = 2, fracionavel = true, ...otherProps } = props;

  const formatCurrency = (value) => {
    if (!fracionavel) {
      return value.replace(/[^\d]/g, "") || "0";
    }

    if (!value || value === "0") {
      if (decimalScale === 0) return "0";
      return "0," + "0".repeat(decimalScale);
    }

    const onlyNums = value.replace(/[^\d]/g, "");
    if (!onlyNums) {
      if (decimalScale === 0) return "0";
      return "0," + "0".repeat(decimalScale);
    }

    const paddedValue = onlyNums.padStart(decimalScale + 1, "0");
    const integerPart =
      paddedValue.slice(0, -decimalScale).replace(/^0+/, "") || "0";
    const decimalPart = paddedValue.slice(-decimalScale);

    return `${integerPart},${decimalPart}`;
  };

  return (
    <NumberFormat
      {...otherProps}
      getInputRef={ref}
      fixedDecimalScale
      decimalScale={fracionavel ? decimalScale : 0}
      inputMode="numeric"
      displayType="input"
      format={(value) => formatCurrency(value)}
      isNumericString
      isAllowed={(values) => {
        const { formattedValue, floatValue } = values;
        return formattedValue === "" || floatValue <= 10000000000;
      }}
    />
  );
});

export default TextFieldMedidas;
